import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom/client';
import './assets/css/main.css';
import './assets/css/main_responsive.css';
import { ThemeProvider } from '@mui/material/styles';
import { SnackbarProvider } from 'notistack';
import AppRoutes from './AppRoutes';
import { Link, BrowserRouter as Router, useLocation } from 'react-router-dom';
import { theme } from './Theme';
import logo from './assets/img/logo.png';
import Menu from './components/Menus/Menu';
import AccountMenu from 'components/Menus/AccountMenu';
import AdminSidebar from 'components/Menus/AdminSidebar';
import Footer from 'Pages/Footer';
import Banner from 'elements/Banner';
import { ApiRequest } from 'GlobalFunctions';
import { Helmet } from 'react-helmet';
import { AuthProvider } from 'contexts/AuthContext';
import { ErrorBoundary } from 'elements/ErrorBoundary';
import ProfileCompletionGate from 'components/Account/ProfileCompletionGate';

const SITE_URL = 'https://buscakine.cl';

const organizationJsonLd = {
  "@context": "https://schema.org",
  "@type": "MedicalOrganization",
  "name": "Buscakine",
  "url": SITE_URL,
  "logo": `${SITE_URL}/favicon/favicon-32x32.png`,
  "description": "Plataforma que conecta pacientes con kinesiólogos a domicilio y en consulta en todo Chile.",
  "address": { "@type": "PostalAddress", "addressCountry": "CL" },
  "areaServed": { "@type": "Country", "name": "Chile" },
  "medicalSpecialty": "Physiotherapy",
  "sameAs": [`${SITE_URL}`]
};

const websiteJsonLd = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "Buscakine",
  "url": SITE_URL,
  "inLanguage": "es-CL",
  "potentialAction": {
    "@type": "SearchAction",
    "target": {
      "@type": "EntryPoint",
      "urlTemplate": `${SITE_URL}/kinesiologos?search={search_term_string}`
    },
    "query-input": "required name=search_term_string"
  }
};

const PageProgressBar = () => {
  const location = useLocation();
  const [active, setActive] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    setActive(true);
    setDone(false);
    const t1 = setTimeout(() => setDone(true), 350);
    const t2 = setTimeout(() => setActive(false), 650);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [location.pathname]);

  if (!active) return null;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, height: '3px',
      zIndex: 9999, overflow: 'hidden', pointerEvents: 'none'
    }}>
      <div style={{
        height: '100%',
        background: 'linear-gradient(90deg, #005F6A, #00A896)',
        width: done ? '100%' : '75%',
        transition: done ? 'width 0.2s ease, opacity 0.3s ease 0.2s' : 'width 0.35s cubic-bezier(0.1, 0.05, 0, 1)',
        opacity: done ? 0 : 1,
        borderRadius: '0 2px 2px 0',
      }} />
    </div>
  );
};

const Site = () => {
  const location = useLocation();
  const isAdmin = localStorage.getItem('role_id') === '100';
  const [adminUser, setAdminUser] = useState({ name: '', lastname: '', image: '' });
  const [seoData, setSeoData] = useState({
    title: 'Buscakine | Kinesiólogos a Domicilio y en Consulta en Chile',
    description: 'Encuentra kinesiólogos a domicilio y en consulta en todo Chile.',
    keywords: '',
  });

  const getSeoData = async () => {
    ApiRequest({
      url: "/settings/details",
      method: "GET",
      setResponse: (response: any) => {
        if (response?.seo) setSeoData(prev => ({ ...prev, ...response.seo }));
      },
    });
  };

  useEffect(() => {
    getSeoData();
    if (isAdmin) {
      ApiRequest({
        url: '/users/details',
        method: 'GET',
        setResponse: (r: any) => setAdminUser({ name: r.name || '', lastname: r.lastname || '', image: r.image || '' }),
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pageUrl = `${SITE_URL}${location.pathname}`;

  return (
    <ThemeProvider theme={theme}>
      <SnackbarProvider maxSnack={3}>
        <Helmet>
          <title>{seoData.title}</title>
          <meta name="description" content={seoData.description} />
          {seoData.keywords && <meta name="keywords" content={seoData.keywords} />}

          {/* Open Graph */}
          <meta property="og:site_name" content="Buscakine" />
          <meta property="og:type" content="website" />
          <meta property="og:url" content={pageUrl} />
          <meta property="og:title" content={seoData.title} />
          <meta property="og:description" content={seoData.description} />
          <meta property="og:locale" content="es_CL" />

          {/* Twitter */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={seoData.title} />
          <meta name="twitter:description" content={seoData.description} />

          {/* Canonical */}
          <link rel="canonical" href={pageUrl} />

          {/* JSON-LD */}
          <script type="application/ld+json">{JSON.stringify(organizationJsonLd)}</script>
          <script type="application/ld+json">{JSON.stringify(websiteJsonLd)}</script>
        </Helmet>

        <PageProgressBar />
        <ProfileCompletionGate />

        {isAdmin ? (
          /* ── Layout admin: sidebar izquierdo + contenido ── */
          <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', background: '#f1f5f9' }}>
            <AdminSidebar
              userName={`${adminUser.name} ${adminUser.lastname}`.trim()}
              userImage={adminUser.image}
            />
            <div style={{ flex: 1, overflow: 'auto' }} id="main_content">
              <AppRoutes setLoading={() => {}} />
            </div>
          </div>
        ) : (
          /* ── Layout público: header top + contenido ── */
          <div id='structure'>
            <div id='header_container'>
              <header>
                <div className='header_left'>
                  <Link to='/'>
                    <img src={logo} alt="Buscakine - Logo" width={100} />
                  </Link>
                  <Menu />
                </div>
                <AccountMenu />
              </header>
            </div>

            <Banner section="global" />

            <div id="main_content">
              <AppRoutes setLoading={() => {}} />
              <Footer />
            </div>
          </div>
        )}
      </SnackbarProvider>
    </ThemeProvider>
  );
};

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

/**
 * Árbol de providers en orden (de afuera hacia adentro):
 * Router → AuthProvider → ThemeProvider → SnackbarProvider → Site
 *
 * - AuthProvider debe estar dentro de Router para poder acceder a hooks de routing en el futuro.
 * - ErrorBoundary en el nivel más alto captura cualquier error de render de la app.
 */
root.render(
  <Router>
    <ErrorBoundary>
      <AuthProvider>
        <Site />
      </AuthProvider>
    </ErrorBoundary>
  </Router>
);
